/**
 * ZefixSearch Component
 * Search Swiss companies via Zefix (Zentraler Firmenindex)
 *
 * Provides autocomplete search for Swiss commercial register entries.
 * Selected companies auto-populate the entity form.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Search, Building2, Loader2, MapPin, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

interface ZefixCompany {
  uid: string;
  name: string;
  legalForm: string;
  legalFormId: number;
  status: 'active' | 'inactive' | 'dissolved';
  canton: string;
  municipality: string;
  registrationDate: string;
  purpose?: string;
  capital?: string;
  address?: {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
  };
}

interface ZefixSearchResponse {
  totalCount: number;
  companies: ZefixCompany[];
  error?: string;
}

interface ZefixSearchProps {
  onSelect: (company: ZefixCompany) => void;
  className?: string;
}

/**
 * Error types for graceful degradation
 */
type ZefixErrorType = 'network' | 'api' | 'timeout' | 'unavailable' | null;

export const ZefixSearch: React.FC<ZefixSearchProps> = ({ onSelect, className = '' }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<ZefixCompany[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ZefixErrorType>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Search Zefix API with graceful degradation
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    const searchZefix = async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        setResults([]);
        setError(null);
        setErrorType(null);
        return;
      }

      setIsLoading(true);
      setError(null);
      setErrorType(null);

      try {
        const response = await fetch(
          `/api/zefix/search?q=${encodeURIComponent(debouncedQuery)}&maxEntries=10&activeOnly=true`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          // Handle different HTTP error codes
          if (response.status === 503 || response.status === 502) {
            setErrorType('unavailable');
            setError('Zefix service is temporarily unavailable');
          } else if (response.status >= 500) {
            setErrorType('api');
            setError('Zefix API error - you can still enter details manually');
          } else {
            setErrorType('api');
            setError('Search failed - try entering details manually');
          }
          setResults([]);
          return;
        }

        const data: ZefixSearchResponse = await response.json();

        if (data.error) {
          setError(data.error);
          setErrorType('api');
          setResults([]);
        } else {
          setResults(data.companies || []);
          setIsOpen(data.companies?.length > 0);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          setErrorType('timeout');
          setError('Search timed out - you can enter details manually');
        } else {
          setErrorType('network');
          setError('Unable to reach Zefix - you can enter details manually');
        }
        setResults([]);
      } finally {
        setIsLoading(false);
        clearTimeout(timeoutId);
      }
    };

    searchZefix();

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [debouncedQuery]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (company: ZefixCompany) => {
    onSelect(company);
    setQuery(company.name);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-success-600 bg-success-50 px-1.5 py-0.5 rounded">
            <CheckCircle className="h-3 w-3" />
            Active
          </span>
        );
      case 'dissolved':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-error-600 bg-error-50 px-1.5 py-0.5 rounded">
            <XCircle className="h-3 w-3" />
            Dissolved
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs text-warning-600 bg-warning-50 px-1.5 py-0.5 rounded">
            Inactive
          </span>
        );
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label className="block text-sm font-medium text-espresso-700 mb-2">
        Search Swiss Commercial Register
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-5 w-5 text-espresso-400 animate-spin" />
          ) : (
            <Search className="h-5 w-5 text-espresso-400" />
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 transition-colors"
          placeholder="Search by company name or UID..."
        />
      </div>

      {/* Helper text */}
      <p className="mt-1 text-xs text-espresso-400">
        Search Zefix to auto-fill entity details from the Swiss commercial register
      </p>

      {/* Error message with graceful degradation */}
      {error && (
        <div className={`mt-2 p-3 rounded-lg flex items-start gap-2 ${
          errorType === 'unavailable' || errorType === 'timeout'
            ? 'bg-warning-50 text-warning-700'
            : 'bg-error-50 text-error-700'
        }`}>
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p>{error}</p>
            {(errorType === 'network' || errorType === 'timeout' || errorType === 'unavailable') && (
              <p className="text-xs mt-1 opacity-75">
                Zefix search is optional - entity creation works without it.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-cream-200 max-h-80 overflow-y-auto">
          {results.map((company, index) => (
            <button
              key={company.uid}
              type="button"
              onClick={() => handleSelect(company)}
              className={`w-full text-left px-4 py-3 hover:bg-cream-50 transition-colors border-b border-cream-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-cream-50' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-1.5 bg-terracotta-50 rounded-lg mt-0.5">
                    <Building2 className="h-4 w-4 text-terracotta-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-espresso-900 truncate">
                      {company.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-sm text-espresso-500">
                      <span className="font-mono text-xs">{company.uid}</span>
                      <span>•</span>
                      <span>{company.legalForm}</span>
                    </div>
                    {company.municipality && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-espresso-400">
                        <MapPin className="h-3 w-3" />
                        {company.municipality}, {company.canton}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(company.status)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {isOpen && query.length >= 2 && !isLoading && results.length === 0 && !error && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-cream-200 p-4 text-center">
          <p className="text-sm text-espresso-500">No companies found matching "{query}"</p>
          <p className="text-xs text-espresso-400 mt-1">
            Try searching with a different name or UID
          </p>
        </div>
      )}
    </div>
  );
};

export default ZefixSearch;
